// ============================================================================
// ENGINE.JS - Pure Referral Evaluation Logic
// No side effects, no storage, no UI concerns
// ============================================================================

/**
 * Check if a field value is considered "filled"
 * @param {*} value - The field value
 * @returns {boolean}
 */
export const isFieldFilled = (value) => {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
};

/**
 * Calculate completeness percentage for required fields
 * @param {Object} pack - The criteria pack
 * @param {Object} answers - Field answers keyed by field ID
 * @returns {number} 0-100
 */
export const calculateCompleteness = (pack, answers) => {
  const requiredFields = pack.fields.filter(f => f.required);
  if (requiredFields.length === 0) return 100;
  
  const filledCount = requiredFields.filter(f => isFieldFilled(answers[f.id])).length;
  return Math.round((filledCount / requiredFields.length) * 100);
};

// Engine version for audit tracking
export const ENGINE_VERSION = '1.1.0';

/**
 * Generate a run ID for audit tracking
 * @returns {string}
 */
export const generateRunId = () => {
  return `RUN-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`.toUpperCase();
};

/**
 * Trace event types for granular audit logging
 * 
 * { t: "eval_start", packId, versionId, engineVersion }
 * { t: "field_checked", fieldId, required, filled, value_type }
 * { t: "requirement_missing", fieldId, label }
 * { t: "rule_fired", ruleId, ruleType, label, critical }
 * { t: "override_applied", reason, triggerRuleId, forcedOutcome }
 * { t: "outcome_set", outcome, completenessScore }
 * { t: "artifact_generated", artifactType, templateVersion }
 * { t: "eval_complete", durationMs }
 */

/**
 * Evaluate a referral and determine outcome
 * Returns decision with full audit trace for compliance
 * 
 * @param {Object} pack - The criteria pack
 * @param {Object} answers - Field answers keyed by field ID
 * @param {string[]} triggeredRedFlagIds - IDs of checked red flags
 * @param {Object} context - Optional context (attemptNumber, previousEventId)
 * @returns {Object} Decision object with audit trace
 */
export const evaluateReferral = (pack, answers, triggeredRedFlagIds = [], context = {}) => {
  // =========================================================================
  // DEFENSIVE VALIDATION
  // =========================================================================
  if (!pack) {
    console.error('evaluateReferral: pack is null or undefined');
    return {
      ok: false,
      error: 'Pack data is missing. Please select a valid pack.',
      outcome: null,
      rationale: 'Evaluation failed: no pack data',
      triggeredRedFlags: [],
      missingRequired: [],
      missingFieldIds: [],
      completenessScore: 0,
      audit: null,
    };
  }
  
  // Ensure pack has required structure with defaults
  const safeFields = pack.fields || [];
  const safeRedFlags = pack.redFlags || [];
  const safeMetadata = pack.metadata || { id: 'unknown', name: 'Unknown Pack', version: '1.0.0' };
  
  const runId = generateRunId();
  const startTime = Date.now();
  const trace = [];
  const reasonCodes = [];
  
  // =========================================================================
  // TRACE EVENT: Evaluation Start
  // =========================================================================
  trace.push({
    t: 'eval_start',
    ts: new Date().toISOString(),
    packId: safeMetadata.id,
    versionId: safeMetadata.version,
    engineVersion: ENGINE_VERSION,
  });
  
  // =========================================================================
  // PHASE 1: Check each field and track missing requirements
  // =========================================================================
  const answeredFieldIds = [];
  const missingRequired = [];
  
  for (const field of safeFields) {
    if (!field || !field.id) continue; // Skip invalid fields
    
    const isFilled = isFieldFilled(answers[field.id]);
    const valueType = isFilled ? (Array.isArray(answers[field.id]) ? 'array' : typeof answers[field.id]) : 'empty';
    
    // Trace each field check
    trace.push({
      t: 'field_checked',
      ts: new Date().toISOString(),
      fieldId: field.id,
      required: field.required || false,
      filled: isFilled,
      valueType,
    });
    
    if (isFilled) {
      answeredFieldIds.push(field.id);
    } else if (field.required) {
      // Track missing required field
      missingRequired.push({ id: field.id, label: field.label || field.id });
      reasonCodes.push(`MISS:${field.id}`);
      
      trace.push({
        t: 'requirement_missing',
        ts: new Date().toISOString(),
        fieldId: field.id,
        label: field.label || field.id,
      });
    }
  }
  
  const completenessScore = calculateCompleteness(pack, answers);
  const missingFieldIds = missingRequired.map(f => f.id);
  
  // =========================================================================
  // PHASE 2: Check red flags (rules that fire)
  // =========================================================================
  const triggeredRedFlags = [];
  let hasOverride = false;
  let overrideReason = null;
  let overrideTrigger = null;
  
  for (const redFlag of safeRedFlags) {
    if (!redFlag || !redFlag.id) continue; // Skip invalid red flags
    
    const isTriggered = triggeredRedFlagIds.includes(redFlag.id);
    
    if (isTriggered) {
      triggeredRedFlags.push({
        id: redFlag.id,
        label: redFlag.label,
        critical: redFlag.critical || false,
      });
      
      reasonCodes.push(`RF:${redFlag.id}`);
      
      // Trace rule fire
      trace.push({
        t: 'rule_fired',
        ts: new Date().toISOString(),
        ruleId: redFlag.id,
        ruleType: 'redFlag',
        label: redFlag.label,
        critical: redFlag.critical || false,
      });
      
      // Check for critical override (forces Urgent)
      if (redFlag.critical && !hasOverride) {
        hasOverride = true;
        overrideReason = 'critical_red_flag';
        overrideTrigger = redFlag.id;
      }
    }
  }
  
  // =========================================================================
  // PHASE 3: Determine outcome with override detection
  // =========================================================================
  let outcome;
  let rationale;
  let rationaleSummary; // Template-like, no PHI
  
  if (triggeredRedFlags.length > 0) {
    outcome = 'Urgent';
    rationale = `Red flag triggered: ${triggeredRedFlags[0].label}`;
    if (triggeredRedFlags.length > 1) {
      rationale += ` (+${triggeredRedFlags.length - 1} more)`;
    }
    rationaleSummary = 'Urgent due to red flag trigger.';
    
    // If critical red flag caused override, trace it
    if (hasOverride) {
      trace.push({
        t: 'override_applied',
        ts: new Date().toISOString(),
        reason: overrideReason,
        triggerRuleId: overrideTrigger,
        forcedOutcome: 'Urgent',
      });
    }
  } else if (missingRequired.length > 0) {
    outcome = 'Not Yet';
    rationale = `Additional workup needed: ${missingRequired.map(f => f.label).join(', ')}`;
    rationaleSummary = 'Incomplete minimum dataset for specialist decision.';
  } else {
    outcome = 'Routine';
    rationale = 'All required data present. No red flags triggered.';
    rationaleSummary = 'Meets minimum dataset; no urgent red flags triggered.';
  }
  
  // Trace outcome
  trace.push({
    t: 'outcome_set',
    ts: new Date().toISOString(),
    outcome,
    completenessScore,
    redFlagsTriggered: triggeredRedFlags.length,
    missingCount: missingRequired.length,
  });
  
  // =========================================================================
  // PHASE 4: Calculate duration and finalize
  // =========================================================================
  const endTime = Date.now();
  const durationMs = endTime - startTime;
  
  trace.push({
    t: 'eval_complete',
    ts: new Date().toISOString(),
    durationMs,
  });
  
  // Build next steps for Not Yet
  const nextSteps = outcome === 'Not Yet' ? buildNextSteps(pack, missingFieldIds) : [];
  
  // =========================================================================
  // BUILD AUDIT OBJECT
  // =========================================================================
  const audit = {
    runId,
    engineVersion: ENGINE_VERSION,
    trace,
    context: {
      packId: safeMetadata.id,
      packName: safeMetadata.name,
      versionId: safeMetadata.version,
      specialtyId: safeMetadata.specialtyId || pack.specialtyId,
      fieldCount: safeFields.length,
      redFlagCount: safeRedFlags.length,
      attemptNumber: context.attemptNumber || 1,
      previousEventId: context.previousEventId || null,
    },
    inputs: {
      answeredFieldIds,
      missingRequiredFieldIds: missingFieldIds,
    },
    evaluation: {
      outcome,
      completenessScore,
      triggeredRedFlags,
      missingRequired,
      hasOverride,
      overrideReason,
      overrideTrigger,
    },
    rationale: {
      full: rationale,
      summary: rationaleSummary,
      reasonCodes,
    },
    timestamps: {
      started: new Date(startTime).toISOString(),
      completed: new Date(endTime).toISOString(),
      durationMs,
    },
  };
  
  // =========================================================================
  // RETURN DECISION WITH AUDIT
  // =========================================================================
  return {
    outcome,
    rationale,
    triggeredRedFlags,
    missingRequired,
    missingFieldIds,
    completenessScore,
    attemptNumber: context.attemptNumber || 1,
    previousEventId: context.previousEventId || null,
    // Not Yet specific
    ...(outcome === 'Not Yet' && {
      nextSteps,
      canRerunGate: true,
      rerunInstructions: 'Complete the steps above, then click "Re-run Gate" to update your referral.',
    }),
    // Audit trace
    audit,
  };
};;

/**
 * Build actionable next steps for Not Yet outcome
 * Maps pack.notYetSteps to missing fields and creates ordered checklist
 * @param {Object} pack - The criteria pack
 * @param {string[]} missingFieldIds - IDs of missing required fields
 * @returns {Array} Ordered next steps with actions and required artifacts
 */
const buildNextSteps = (pack, missingFieldIds) => {
  const steps = [];
  
  // If pack has structured notYetSteps, use them
  if (pack.notYetSteps && Array.isArray(pack.notYetSteps)) {
    // Handle both old format (string array) and new format (object array)
    pack.notYetSteps.forEach((step, idx) => {
      if (typeof step === 'string') {
        // Old format: simple string
        steps.push({
          stepId: `step_${idx + 1}`,
          label: step,
          description: null,
          requiredArtifacts: [],
          linkedFields: [],
          completed: false,
        });
      } else if (typeof step === 'object') {
        // New format: structured object
        steps.push({
          stepId: step.stepId || `step_${idx + 1}`,
          label: step.label || step.toString(),
          description: step.description || null,
          requiredArtifacts: step.requiredArtifacts || [],
          linkedFields: step.linkedFields || [],
          completed: false,
        });
      }
    });
  }
  
  // Add steps for missing fields that aren't covered by explicit notYetSteps
  const coveredFields = steps.flatMap(s => s.linkedFields);
  const uncoveredMissing = missingFieldIds.filter(id => !coveredFields.includes(id));
  
  if (uncoveredMissing.length > 0) {
    // Find field labels
    const uncoveredLabels = pack.fields
      .filter(f => uncoveredMissing.includes(f.id))
      .map(f => f.label);
    
    if (uncoveredLabels.length > 0) {
      steps.push({
        stepId: 'complete_fields',
        label: 'Complete required fields',
        description: `Fill in: ${uncoveredLabels.join(', ')}`,
        requiredArtifacts: [],
        linkedFields: uncoveredMissing,
        completed: false,
      });
    }
  }
  
  return steps;
};

/**
 * Generate output artifacts (note + patient message)
 * Called only at output step, NEVER stored
 * @param {Object} pack - The criteria pack
 * @param {Object} answers - Field answers
 * @param {Object} decision - The evaluation decision
 * @param {Object} context - Additional context (localId, telehealth, etc)
 * @returns {Object} { referralNote, patientMessage }
 */
export const generateOutputs = (pack, answers, decision, context = {}) => {
  const { localId, telehealth } = context;
  
  // Defensive: ensure pack has required structure
  if (!pack || !decision) {
    console.error('generateOutputs: Missing pack or decision');
    return {
      referralNote: 'Error: Unable to generate referral note',
      patientMessage: '',
      error: 'Missing pack or decision data',
    };
  }
  
  // Defensive: ensure metadata exists
  const metadata = pack.metadata || {};
  const specialty = metadata.specialty || metadata.specialtyId || 'SPECIALIST';
  const packName = metadata.name || metadata.id || 'Unknown Pack';
  const packVersion = metadata.version || '1.0.0';
  
  // Build referral note (ephemeral, for copy/paste only)
  let referralNote = `REFERRAL TO ${specialty.toUpperCase()}\n`;
  referralNote += `Pack: ${packName} v${packVersion}\n`;
  if (localId) referralNote += `Local ID: ${localId}\n`;
  if (telehealth?.visitType) referralNote += `Visit Type: ${telehealth.visitType}\n`;
  if (telehealth?.state) referralNote += `State: ${telehealth.state}\n`;
  referralNote += `\nDECISION: ${decision.outcome || 'Unknown'}\n`;
  referralNote += `Rationale: ${decision.rationale || 'No rationale provided'}\n`;
  referralNote += `Completeness: ${decision.completenessScore || 0}%\n`;
  referralNote += `\n--- CLINICAL DATA ---\n`;
  
  // Defensive: ensure fields array exists
  const fields = pack.fields || [];
  fields.forEach(field => {
    if (!field || !field.id) return;
    const value = answers[field.id];
    const displayValue = Array.isArray(value) ? value.join(', ') : (value || 'N/A');
    referralNote += `${field.label || field.id}: ${displayValue}\n`;
  });
  
  // Get patient message based on outcome - with defensive guards
  const outcomeKey = decision.outcome === 'Not Yet' ? 'NotYet' : (decision.outcome || 'Routine');
  
  // Defensive: handle missing patientMessages
  const patientMessages = pack.patientMessages || {};
  let patientMessage = patientMessages[outcomeKey] || '';
  
  // Fallback: try lowercase key
  if (!patientMessage && outcomeKey) {
    patientMessage = patientMessages[outcomeKey.toLowerCase()] || '';
  }
  
  // Fallback: try uppercase key
  if (!patientMessage && outcomeKey) {
    patientMessage = patientMessages[outcomeKey.toUpperCase()] || '';
  }
  
  return {
    referralNote,
    patientMessage,
  };
};

/**
 * Create a metric event for storage (NON-PHI ONLY)
 * This is the ONLY function that should feed into saveMetricEvent
 * 
 * REQUIRED: tenantId, specialtyId, packId, versionId, submittedBy
 * Will THROW if any required field is missing or empty
 * 
 * @param {Object} params
 * @returns {Object} Clean metric event
 * @throws {Error} If required fields are missing
 */
export const createMetricEvent = ({
  tenantId,
  specialtyId,
  packId,
  versionId,
  outcome,
  completenessScore,
  redFlagsTriggeredCount,
  triggeredRedFlagIds, // NEW: Array of red flag IDs that were triggered
  missingFieldIds, // IDs only, never values
  telehealthType,
  telehealthState,
  submittedBy, // { id, role } - no name for privacy
  attemptNumber, // For re-run tracking
  previousEventId, // Link to previous attempt
}) => {
  // ===== VALIDATION: Required fields must be non-empty strings =====
  if (!tenantId || typeof tenantId !== 'string' || tenantId.trim() === '') {
    throw new Error('createMetricEvent: tenantId is required and must be a non-empty string');
  }
  if (!specialtyId || typeof specialtyId !== 'string' || specialtyId.trim() === '') {
    throw new Error('createMetricEvent: specialtyId is required and must be a non-empty string');
  }
  if (!packId || typeof packId !== 'string' || packId.trim() === '') {
    throw new Error('createMetricEvent: packId is required and must be a non-empty string');
  }
  if (!versionId || typeof versionId !== 'string' || versionId.trim() === '') {
    throw new Error('createMetricEvent: versionId is required and must be a non-empty string');
  }
  if (!submittedBy?.id || typeof submittedBy.id !== 'string' || submittedBy.id.trim() === '') {
    throw new Error('createMetricEvent: submittedBy.id is required and must be a non-empty string');
  }
  if (!outcome || typeof outcome !== 'string') {
    throw new Error('createMetricEvent: outcome is required and must be a string');
  }
  
  return {
    // Required identifiers (all validated above)
    tenantId,
    specialtyId,
    packId,
    versionId,
    
    // Decision data (non-PHI)
    outcome,
    completenessScore: typeof completenessScore === 'number' ? completenessScore : 0,
    redFlagsTriggeredCount: typeof redFlagsTriggeredCount === 'number' ? redFlagsTriggeredCount : 0,
    triggeredRedFlagIds: Array.isArray(triggeredRedFlagIds) ? triggeredRedFlagIds : [], // NEW
    missingFieldIds: Array.isArray(missingFieldIds) ? missingFieldIds : [],
    
    // Context (non-PHI, optional)
    telehealthType: telehealthType || null,
    telehealthState: telehealthState || null,
    
    // Submitter (minimal, non-PHI)
    submittedById: submittedBy.id,
    submittedByRole: submittedBy?.role || null,
    
    // Re-run tracking (for Not Yet → Ready conversion analytics)
    attemptNumber: typeof attemptNumber === 'number' ? attemptNumber : 1,
    previousEventId: previousEventId || null,
    
    // Timestamp (client-side, server will add savedAt)
    timestamp: new Date().toISOString(),
  };
};

/**
 * Helper to extract metric event params from pack and decision
 * Use this when calling createMetricEvent to ensure all required fields are present
 * 
 * @param {Object} pack - The CriteriaPack (must have metadata.specialtyId)
 * @param {Object} decision - The evaluation result
 * @param {Object} context - { tenantId, user, telehealth }
 * @returns {Object} Params ready for createMetricEvent
 * @throws {Error} If pack is missing specialtyId
 */
export const buildMetricParams = (pack, decision, context) => {
  const { tenantId, user, telehealth } = context;
  
  // Extract identifiers with multiple fallbacks
  const specialtyId = pack?.metadata?.specialtyId || pack?.specialtyId;
  const packId = pack?.metadata?.id || pack?.packId || pack?.id;
  const versionId = pack?.metadata?.version || pack?.versionId;
  
  // Validate required fields with detailed error messages
  if (!specialtyId) {
    console.error('[ERROR] buildMetricParams: Missing specialtyId. Pack:', pack);
    throw new Error('buildMetricParams: pack is missing specialtyId (checked metadata.specialtyId and pack.specialtyId)');
  }
  if (!packId) {
    console.error('[ERROR] buildMetricParams: Missing packId. Pack:', pack);
    throw new Error('buildMetricParams: pack is missing packId (checked metadata.id and pack.packId)');
  }
  if (!versionId) {
    console.error('[ERROR] buildMetricParams: Missing versionId. Pack:', pack);
    throw new Error('buildMetricParams: pack is missing versionId (checked metadata.version and pack.versionId)');
  }
  
  return {
    tenantId,
    specialtyId,
    packId,
    versionId,
    outcome: decision.outcome,
    completenessScore: decision.completenessScore,
    redFlagsTriggeredCount: decision.triggeredRedFlags?.length || 0,
    triggeredRedFlagIds: decision.triggeredRedFlags?.map(rf => rf.id) || [],
    missingFieldIds: decision.missingRequired?.map(f => f.id) || [],
    telehealthType: telehealth?.visitType || null,
    telehealthState: telehealth?.state || null,
    submittedBy: { id: user?.id, role: user?.role },
  };
};

/**
 * Generate a local ID for referral tracking
 * @param {string} tenantId
 * @returns {string}
 */
export const generateLocalId = (tenantId) => {
  const prefix = (tenantId || 'REF').substring(0, 3).toUpperCase();
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${prefix}-${timestamp}-${random}`;
};

/**
 * Generate a generic unique ID
 * @param {string} prefix
 * @returns {string}
 */
export const generateId = (prefix = 'ID') => {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`.toUpperCase();
};

/**
 * Create an audit log entry for storage
 * This captures the decision trace for compliance and review
 * 
 * IMPORTANT: Does NOT contain PHI - only decision metadata and trace events
 * 
 * @param {Object} params
 * @param {Object} params.audit - The audit object from evaluateReferral
 * @param {Object} params.decision - The decision result (outcome, rationale, etc.)
 * @param {Object} params.context - Additional context (tenantId, userId, etc.)
 * @param {Object} params.artifacts - Optional artifacts metadata (not content)
 * @returns {Object} Audit log entry ready for storage
 */
export const createAuditLog = ({
  audit,
  decision,
  context,
  artifacts = {},
}) => {
  if (!audit?.runId) {
    throw new Error('createAuditLog: audit.runId is required');
  }
  if (!context?.tenantId) {
    throw new Error('createAuditLog: context.tenantId is required');
  }
  if (!context?.userId) {
    throw new Error('createAuditLog: context.userId is required');
  }
  
  return {
    // Document type for querying
    type: 'referral_decision_audit',
    
    // Identifiers
    runId: audit.runId,
    tenantId: context.tenantId,
    specialtyId: audit.context?.specialtyId || context.specialtyId,
    
    // Actor (no displayName for privacy)
    actor: {
      userId: context.userId,
      role: context.userRole,
    },
    
    // Engine and pack context
    context: {
      packId: audit.context?.packId,
      packName: audit.context?.packName,
      versionId: audit.context?.versionId,
      specialtyId: audit.context?.specialtyId,
      engineVersion: audit.engineVersion || ENGINE_VERSION,
      attemptNumber: audit.context?.attemptNumber || 1,
      previousEventId: audit.context?.previousEventId || null,
    },
    
    // Timestamps
    timestamps: audit.timestamps,
    
    // Inputs (non-PHI - just field IDs)
    inputs: {
      answeredFieldIds: audit.inputs?.answeredFieldIds || [],
      missingRequiredFieldIds: audit.inputs?.missingRequiredFieldIds || [],
    },
    
    // Evaluation result
    evaluation: {
      outcome: decision.outcome,
      completenessScore: decision.completenessScore,
      triggeredRedFlags: decision.triggeredRedFlags || [],
      missingRequired: decision.missingRequired || [],
      hasOverride: audit.evaluation?.hasOverride || false,
      overrideReason: audit.evaluation?.overrideReason || null,
      overrideTrigger: audit.evaluation?.overrideTrigger || null,
      rationale: {
        full: audit.rationale?.full || decision.rationale,
        summary: audit.rationale?.summary || null,
        reasonCodes: audit.rationale?.reasonCodes || [],
      },
    },
    
    // Artifacts metadata (not content)
    artifacts: {
      referralNote: artifacts.referralNote ? {
        generated: true,
        templateVersion: artifacts.templateVersion || null,
      } : null,
      patientMessage: artifacts.patientMessage ? {
        generated: true,
      } : null,
    },
    
    // Full granular trace for detailed audit
    trace: audit.trace,
    
    // Storage metadata
    createdAt: new Date().toISOString(),
  };
};

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  ENGINE_VERSION,
  isFieldFilled,
  calculateCompleteness,
  evaluateReferral,
  generateOutputs,
  createMetricEvent,
  buildMetricParams,
  generateLocalId,
  generateId,
  generateRunId,
  createAuditLog,
};
